import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import deliveryService from '../../services/deliveryService';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import { ROUTES } from '../../routes';
import ConfirmRemovePhotoModal from './ConfirmRemovePhotoModal';
import MessageModal from '../shared/MessageModal';
import { VehicleIcon } from '../Layout/icons';
import { getDeliveryStatusMeta } from './statusConfig';
import DeliveryStatusBadge from './DeliveryStatusBadge';
import CopyableText from '../shared/CopyableText';
import {
    PER_ROW,
    TimelineSection,
    TimelineTitle,
    TrackHint,
    TrackWrap,
    TrackRowsStack,
    TrackRow,
    TrackRoadStrip,
    TrackLaneStrip,
    TrackRowGrid,
    VehicleLayerRow,
    VehicleMover,
    ExhaustSmoke,
    VehicleOnWaypoint,
    WaypointCell,
    WaypointInteractiveWrap,
    WaypointHit,
    WaypointDot,
    WaypointCaption,
    InfoPopover,
    InfoPopoverPortal,
    TimelineDetailBackdrop,
    DetailHeader,
    DetailTime,
    DetailDescription,
    DetailUser,
    DetailMetaBlock,
    DetailMetaLabel,
    DetailMetaText,
    DetailMetaHint,
    DetailDriverLink,
    PhotoGallerySection,
    PhotoGalleryLabel,
    PhotoGalleryGrid,
    PhotoGalleryThumbWrapper,
    PhotoThumbBtn,
    PhotoLightboxBackdrop,
    PhotoLightboxImg,
} from './DeliveryStatusTimeline.styles';
import { PhotoRemoveBtn } from './DeliveryStatusUpdateSection.styles';

/** Pequeno avanço visual do caminhão em relação ao centro do ponto (px); mantém baixo para não invadir a coluna vizinha. */
const VEHICLE_AHEAD_PX = 6;

const TRUCK_SEGMENT_MS = 5000;
const BETWEEN_ROWS_MS = 500;

function doubleRequestAnimationFrame() {
    return new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
}

function sleep(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

const COMPACT_MEDIA = '(max-width: 640px)';

function useTimelineCompact() {
    const [compact, setCompact] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia(COMPACT_MEDIA).matches : false
    );
    useEffect(() => {
        const mq = window.matchMedia(COMPACT_MEDIA);
        const onChange = () => setCompact(mq.matches);
        mq.addEventListener('change', onChange);
        onChange();
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return compact;
}

function formatDateTime(value) {
    if (!value) return '—';
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '—';
    }
}

/** Centro horizontal da coluna `col` (0..2) numa grelha de 3 colunas, em %. */
function colCenterPercent(col) {
    return ((col + 0.5) / PER_ROW) * 100;
}

function chunkHistory(items, size) {
    const out = [];
    for (let i = 0; i < items.length; i += size) {
        out.push(items.slice(i, i + size));
    }
    return out;
}

/** ID do motorista/colaborador no evento de histórico (ou fallback da entrega). */
function resolveHistoryDriverId(entry, fallbackDriverId) {
    const raw =
        entry?.driverId ??
        entry?.driver?.id ??
        entry?.motorista?.id ??
        entry?.driverEmployeeId ??
        fallbackDriverId;
    if (raw == null || String(raw).trim() === '') return null;
    const id = Number(raw);
    return Number.isNaN(id) ? null : id;
}

function resolveHistoryAction(entry) {
    const action = entry?.action;
    if (!action) return null;
    if (typeof action === 'string') {
        const label = action.trim();
        return label ? { label, description: '' } : null;
    }
    if (typeof action !== 'object') return null;
    const label = String(action.status || action.value || '').trim();
    const description = String(action.description || '').trim();
    if (!label && !description) return null;
    return { label: label || 'Ação', description };
}

/**
 * Uma pista independente por linha (até 3 eventos); detalhes em caixinha junto ao ponto (hover / clique).
 *
 * @param {{ history: Array, deliveryId?: string|number, customerIdForApi?: string|null, driverId?: string|number|null, canManagePhotos?: boolean, onPhotosChanged?: () => void|Promise<void> }} props
 */
export default function DeliveryStatusTimeline({
    history,
    deliveryId,
    customerIdForApi,
    driverId = null,
    canManagePhotos = false,
    onPhotosChanged,
}) {
    const navigate = useNavigate();
    const { isTransportCompany } = useCustomerSelector();
    const isCompact = useTimelineCompact();

    const sorted = useMemo(
        () =>
            !Array.isArray(history)
                ? []
                : [...history].sort(
                      (a, b) => new Date(a.occurrenceDateTime) - new Date(b.occurrenceDateTime)
                  ),
        [history]
    );

    const chunks = useMemo(() => chunkHistory(sorted, PER_ROW), [sorted]);

    const [pinnedIndex, setPinnedIndex] = useState(null);
    const [hoverIndex, setHoverIndex] = useState(null);
    const [lightboxUrl, setLightboxUrl] = useState(null);
    const [photoToRemove, setPhotoToRemove] = useState(null);
    const [removingPhoto, setRemovingPhoto] = useState(false);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });
    const leaveTimerRef = useRef(null);

    const singleRowLayout = chunks.length === 1;
    const lastGlobalIndex = sorted.length > 0 ? sorted.length - 1 : 0;

    const [truckRowIndex, setTruckRowIndex] = useState(0);
    const [introComplete, setIntroComplete] = useState(false);
    const [vehiclePos, setVehiclePos] = useState(() => ({
        pct: sorted.length > 0 ? colCenterPercent(0) : 0,
        aheadPx: 0,
    }));
    const [vehicleMotion, setVehicleMotion] = useState(false);

    const historyAnimKey = useMemo(
        () => sorted.map((h) => `${h.id ?? ''}:${h.occurrenceDateTime ?? ''}`).join('|'),
        [sorted]
    );

    useEffect(() => {
        if (sorted.length === 0) return undefined;

        let cancelled = false;

        const reduced =
            typeof window !== 'undefined' &&
            window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

        if (reduced) {
            setVehicleMotion(false);
            if (sorted.length <= 1) {
                setVehiclePos({ pct: colCenterPercent(0), aheadPx: VEHICLE_AHEAD_PX });
            } else {
                const lastCol = (sorted.length - 1) % PER_ROW;
                setVehiclePos({ pct: colCenterPercent(lastCol), aheadPx: VEHICLE_AHEAD_PX });
            }
            setTruckRowIndex(0);
            setIntroComplete(true);
            return () => {
                cancelled = true;
            };
        }

        const runIntro = async () => {
            setIntroComplete(false);
            setTruckRowIndex(0);
            setVehicleMotion(false);

            if (sorted.length <= 1) {
                setVehiclePos({ pct: colCenterPercent(0), aheadPx: VEHICLE_AHEAD_PX });
                setIntroComplete(true);
                return;
            }

            if (chunks.length === 1) {
                const endPct = colCenterPercent(sorted.length - 1);
                setTruckRowIndex(0);
                setVehiclePos({ pct: colCenterPercent(0), aheadPx: 0 });
                await doubleRequestAnimationFrame();
                if (cancelled) return;
                setVehicleMotion(true);
                setVehiclePos({ pct: endPct, aheadPx: VEHICLE_AHEAD_PX });
                await sleep(TRUCK_SEGMENT_MS);
                if (cancelled) return;
                setVehicleMotion(false);
                setIntroComplete(true);
                return;
            }

            for (let r = 0; r < chunks.length; r++) {
                if (cancelled) return;
                const chunk = chunks[r];
                const lastC = chunk.length - 1;
                const startPct = colCenterPercent(0);
                const endPct = colCenterPercent(lastC);

                setTruckRowIndex(r);
                setVehicleMotion(false);
                setVehiclePos({ pct: startPct, aheadPx: 0 });
                await doubleRequestAnimationFrame();
                if (cancelled) return;

                if (lastC === 0) {
                    await sleep(2080);
                    if (cancelled) return;
                    setVehiclePos({ pct: endPct, aheadPx: VEHICLE_AHEAD_PX });
                    await sleep(1800);
                    if (cancelled) return;
                } else {
                    setVehicleMotion(true);
                    setVehiclePos({ pct: endPct, aheadPx: VEHICLE_AHEAD_PX });
                    await sleep(TRUCK_SEGMENT_MS);
                    if (cancelled) return;
                    setVehicleMotion(false);
                }

                if (r < chunks.length - 1) {
                    await sleep(BETWEEN_ROWS_MS);
                }
            }

            if (cancelled) return;
            setIntroComplete(true);
        };

        runIntro();

        return () => {
            cancelled = true;
        };
    }, [historyAnimKey, sorted.length, chunks]);

    const clearLeaveTimer = useCallback(() => {
        if (leaveTimerRef.current != null) {
            window.clearTimeout(leaveTimerRef.current);
            leaveTimerRef.current = null;
        }
    }, []);

    const onWaypointEnter = useCallback(
        (index) => {
            clearLeaveTimer();
            setHoverIndex(index);
        },
        [clearLeaveTimer]
    );

    const onWaypointLeave = useCallback(() => {
        clearLeaveTimer();
        leaveTimerRef.current = window.setTimeout(() => setHoverIndex(null), 220);
    }, [clearLeaveTimer]);

    useEffect(
        () => () => {
            clearLeaveTimer();
        },
        [clearLeaveTimer]
    );

    const openDetailIndex = hoverIndex != null ? hoverIndex : pinnedIndex;

    const closeDetail = useCallback(() => {
        setPinnedIndex(null);
        setHoverIndex(null);
    }, []);

    const togglePin = useCallback((index) => {
        setPinnedIndex((prev) => (prev === index ? null : index));
    }, []);

    useEffect(() => {
        if (!isCompact || openDetailIndex == null) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') closeDetail();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isCompact, openDetailIndex, closeDetail]);

    useEffect(() => {
        if (!lightboxUrl) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setLightboxUrl(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightboxUrl]);

    const handleConfirmRemovePhoto = async () => {
        if (!photoToRemove?.id || !deliveryId || !customerIdForApi) return;
        setRemovingPhoto(true);
        try {
            await deliveryService.deleteDeliveryImage(
                deliveryId,
                photoToRemove.id,
                customerIdForApi
            );
            setPhotoToRemove(null);
            if (onPhotosChanged) {
                await onPhotosChanged();
            }
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao remover a foto.',
            });
        } finally {
            setRemovingPhoto(false);
        }
    };

    const openDriverCollaborator = useCallback(
        (targetDriverId) => {
            if (targetDriverId == null) return;
            navigate(ROUTES.COLABORADORES, {
                state: {
                    openEmployeeId: targetDriverId,
                    manageOwnData: isTransportCompany,
                },
            });
        },
        [isTransportCompany, navigate]
    );

    const renderDetailBody = (entry) => {
        const statusValue =
            entry.updatedTo?.value || (typeof entry.updatedTo === 'string' ? entry.updatedTo : '');
        const description = entry.updatedTo?.description || '';
        const user = entry.lastUpdateBy || entry.createdBy || '';
        const photos = Array.isArray(entry.photos) ? entry.photos : [];
        const driverComment =
            entry.driverComment != null ? String(entry.driverComment).trim() : '';
        const actionMeta = resolveHistoryAction(entry);
        const entryDriverId = resolveHistoryDriverId(entry, driverId);
        return (
            <>
                <DetailHeader>
                    <DeliveryStatusBadge state={statusValue} compact />
                </DetailHeader>
                <DetailTime dateTime={entry.occurrenceDateTime}>
                    {formatDateTime(entry.occurrenceDateTime)}
                </DetailTime>
                {description ? <DetailDescription>{description}</DetailDescription> : null}
                {user ? (
                    <DetailUser>
                        Atualizado por:{' '}
                        <CopyableText text={user} displayText={user.toLowerCase()} />
                    </DetailUser>
                ) : null}
                {driverComment ? (
                    <DetailMetaBlock>
                        <DetailMetaLabel>Comentário do motorista</DetailMetaLabel>
                        <DetailMetaText>{driverComment}</DetailMetaText>
                    </DetailMetaBlock>
                ) : null}
                {actionMeta ? (
                    <DetailMetaBlock>
                        <DetailMetaLabel>Ação</DetailMetaLabel>
                        <DetailMetaText>{actionMeta.label}</DetailMetaText>
                        {actionMeta.description ? (
                            <DetailMetaHint>{actionMeta.description}</DetailMetaHint>
                        ) : null}
                    </DetailMetaBlock>
                ) : null}
                {entryDriverId != null ? (
                    <DetailMetaBlock>
                        <DetailMetaLabel>Motorista / colaborador</DetailMetaLabel>
                        <DetailDriverLink
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                openDriverCollaborator(entryDriverId);
                            }}
                        >
                            Consultar colaborador #{entryDriverId}
                        </DetailDriverLink>
                    </DetailMetaBlock>
                ) : null}
                {photos.length > 0 ? (
                    <PhotoGallerySection>
                        <PhotoGalleryLabel>Fotos ({photos.length})</PhotoGalleryLabel>
                        <PhotoGalleryGrid>
                            {photos.map((photo) => (
                                <PhotoGalleryThumbWrapper key={photo.id}>
                                    <PhotoThumbBtn
                                        type="button"
                                        aria-label="Ver foto em tamanho real"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setLightboxUrl(photo.url);
                                        }}
                                    >
                                        <img src={photo.url} alt="" loading="lazy" />
                                    </PhotoThumbBtn>
                                    {canManagePhotos && photo.id != null ? (
                                        <PhotoRemoveBtn
                                            type="button"
                                            aria-label="Remover foto"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setPhotoToRemove(photo);
                                            }}
                                        >
                                            ×
                                        </PhotoRemoveBtn>
                                    ) : null}
                                </PhotoGalleryThumbWrapper>
                            ))}
                        </PhotoGalleryGrid>
                    </PhotoGallerySection>
                ) : null}
            </>
        );
    };

    if (sorted.length === 0) return null;

    const portalEntry = isCompact && openDetailIndex != null ? sorted[openDetailIndex] : null;
    const portalLabel = portalEntry
        ? (() => {
              const v =
                  portalEntry.updatedTo?.value ||
                  (typeof portalEntry.updatedTo === 'string' ? portalEntry.updatedTo : '');
              const m = getDeliveryStatusMeta(v);
              return m.label || v || '—';
          })()
        : '';
    const portalId = openDetailIndex != null ? `delivery-waypoint-info-${openDetailIndex}` : '';

    const renderWaypoint = (entry, globalIndex) => {
        const isCurrent = globalIndex === lastGlobalIndex;
        const statusValue =
            entry.updatedTo?.value || (typeof entry.updatedTo === 'string' ? entry.updatedTo : '');
        const meta = getDeliveryStatusMeta(statusValue);
        const label = meta.label || statusValue || '—';
        const showDetail = openDetailIndex === globalIndex;
        const showTruckHere =
            !singleRowLayout && introComplete && globalIndex === lastGlobalIndex;
        const popoverId = `delivery-waypoint-info-${globalIndex}`;
        const usePortalSheet = isCompact && showDetail;

        return (
            <WaypointCell key={entry.id ?? globalIndex}>
                {showTruckHere ? (
                    <VehicleOnWaypoint aria-hidden>
                        <VehicleIcon solidWhite />
                    </VehicleOnWaypoint>
                ) : null}
                <WaypointInteractiveWrap
                    onMouseEnter={() => onWaypointEnter(globalIndex)}
                    onMouseLeave={onWaypointLeave}
                >
                    <WaypointHit
                        type="button"
                        $active={showDetail}
                        aria-expanded={showDetail}
                        aria-controls={popoverId}
                        aria-label={`Etapa ${globalIndex + 1}: ${label}`}
                        onClick={() => togglePin(globalIndex)}
                    >
                        <WaypointDot
                            $colorToken={meta.colorToken}
                            $isCurrent={isCurrent}
                            aria-hidden
                        />
                        <WaypointCaption title={isCompact ? undefined : label}>{label}</WaypointCaption>
                    </WaypointHit>
                    {showDetail && !usePortalSheet ? (
                        <InfoPopover
                            id={popoverId}
                            role="region"
                            aria-label={`Detalhes: ${label}`}
                            onMouseEnter={() => onWaypointEnter(globalIndex)}
                            onMouseLeave={onWaypointLeave}
                        >
                            {renderDetailBody(entry)}
                        </InfoPopover>
                    ) : null}
                </WaypointInteractiveWrap>
            </WaypointCell>
        );
    };

    return (
        <TimelineSection>
            <TimelineTitle>Histórico de atualizações</TimelineTitle>
            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                />
            )}
            {typeof document !== 'undefined' && lightboxUrl
                ? createPortal(
                      <PhotoLightboxBackdrop
                          type="button"
                          aria-label="Fechar foto"
                          onClick={() => setLightboxUrl(null)}
                      >
                          <PhotoLightboxImg src={lightboxUrl} alt="Foto ampliada" />
                      </PhotoLightboxBackdrop>,
                      document.body
                  )
                : null}
            {typeof document !== 'undefined' && portalEntry
                ? createPortal(
                      <>
                          <TimelineDetailBackdrop
                              type="button"
                              aria-label="Fechar detalhes"
                              onClick={closeDetail}
                          />
                          <InfoPopoverPortal
                              id={portalId}
                              role="region"
                              aria-label={`Detalhes: ${portalLabel}`}
                              onMouseEnter={() =>
                                  openDetailIndex != null && onWaypointEnter(openDetailIndex)
                              }
                              onMouseLeave={onWaypointLeave}
                          >
                              {renderDetailBody(portalEntry)}
                          </InfoPopoverPortal>
                      </>,
                      document.body
                  )
                : null}
            {photoToRemove ? (
                <ConfirmRemovePhotoModal
                    onConfirm={handleConfirmRemovePhoto}
                    onClose={() => !removingPhoto && setPhotoToRemove(null)}
                    isRemoving={removingPhoto}
                />
            ) : null}
            <TrackHint>
                {isCompact
                    ? 'Toque num ponto da linha do tempo para ver data, descrição e quem atualizou. Toque fora do painel escuro para fechar.'
                    : 'Cada linha mostra até três atualizações numa pista própria. Passe o mouse ou clique num ponto para abrir a caixinha com os detalhes.'}
            </TrackHint>
            <TrackWrap>
                <TrackRowsStack>
                    {chunks.map((chunk, rowIndex) => (
                        <TrackRow key={`row-${rowIndex}`}>
                            <TrackRoadStrip aria-hidden />
                            <TrackLaneStrip aria-hidden />
                            {(singleRowLayout || !introComplete) && (
                                <VehicleLayerRow
                                    aria-hidden
                                    $visible={singleRowLayout ? true : truckRowIndex === rowIndex}
                                >
                                    <VehicleMover
                                        $leftPct={vehiclePos.pct}
                                        $aheadPx={vehiclePos.aheadPx}
                                        $motion={vehicleMotion}
                                    >
                                        <ExhaustSmoke $active={vehicleMotion} aria-hidden>
                                            <span />
                                            <span />
                                            <span />
                                        </ExhaustSmoke>
                                        <VehicleIcon solidWhite />
                                    </VehicleMover>
                                </VehicleLayerRow>
                            )}
                            <TrackRowGrid>
                                {chunk.map((entry, colIndex) => {
                                    const globalIndex = rowIndex * PER_ROW + colIndex;
                                    return renderWaypoint(entry, globalIndex);
                                })}
                                {chunk.length < PER_ROW
                                    ? Array.from({ length: PER_ROW - chunk.length }, (_, i) => (
                                          <WaypointCell key={`pad-${rowIndex}-${i}`} aria-hidden />
                                      ))
                                    : null}
                            </TrackRowGrid>
                        </TrackRow>
                    ))}
                </TrackRowsStack>
            </TrackWrap>
        </TimelineSection>
    );
}
